// frontend/src/hooks/useScrollToCurrentOrder.ts
import { useEffect, useRef, type RefObject } from 'react';
import type { ProductionOrder } from '@/types/productionSequence';
import { computeInitialScrollTarget } from '@/utils/sequenceCalculations';

interface Options {
  orders: ProductionOrder[];                 // lista ordenada completa (visibleOrders)
  isLoading: boolean;
  scrollRef: RefObject<HTMLElement | null>;  // contenedor scrollable
  pageSize?: number;                          // 200 en tablas; omitido en móvil
  page?: number;                              // página actual (tablas)
  setPage?: (page: number) => void;           // setter de página (tablas)
  enabled?: boolean;                          // default true; false = no-op (vista no montada)
  resetKey?: unknown;                         // al cambiar, se reposiciona una vez (p.ej. la línea)
}

/**
 * Posiciona el scroll en la primera producción no terminada (con 2 terminadas de
 * contexto encima). Reutilizable por las 3 vistas de secuencia. Si todo está
 * terminado, scroll al final.
 *
 * Reposiciona UNA vez por cada valor de `resetKey` (la línea/ruta seleccionada): al
 * entrar (línea autoseleccionada) y cada vez que se cambia de línea. Dentro de la
 * misma línea, ordenar/filtrar/reordenar NO reposiciona (el guard sigue marcado).
 *
 * Requiere que cada fila/tarjeta del contenedor exponga `data-seq-row="<order.id>"`.
 */
export function useScrollToCurrentOrder({
  orders, isLoading, scrollRef, pageSize, page, setPage, enabled = true, resetKey,
}: Options): void {
  const doneRef = useRef(false);
  const keyRef = useRef(resetKey);

  useEffect(() => {
    // Nuevo contexto (p.ej. cambio de línea) → rehabilita un reposicionado.
    if (keyRef.current !== resetKey) { keyRef.current = resetKey; doneRef.current = false; }
    if (!enabled || doneRef.current || isLoading || orders.length === 0) return;

    const target = computeInitialScrollTarget(orders, pageSize);
    if (target.mode === 'none') return;

    // Si el ancla cae en otra página, navegar primero.
    if (target.mode === 'anchor' && pageSize && setPage && page !== target.page) {
      setPage(target.page);
    }

    // El scroll se aplica tras el render (y tras el posible cambio de página).
    // Reintentamos por frames hasta que la fila ancla exista en el DOM.
    let raf = 0;
    let tries = 0;
    const tick = () => {
      const c = scrollRef.current;
      if (!c) return;
      if (target.mode === 'bottom') { c.scrollTop = c.scrollHeight; doneRef.current = true; return; }
      const row = c.querySelector<HTMLElement>(`[data-seq-row="${CSS.escape(target.anchorOrderId)}"]`);
      if (row) {
        // Offset del header fijo medido del <thead> (0 si no existe, p.ej. móvil).
        const thead = c.querySelector('thead');
        const off = thead ? thead.getBoundingClientRect().height : 0;
        const cRect = c.getBoundingClientRect();
        const rRect = row.getBoundingClientRect();
        c.scrollTop += (rRect.top - cRect.top) - off;
        doneRef.current = true;
        return;
      }
      if (tries++ < 30) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // doneRef se marca SÓLO cuando el scroll se ejecuta (dentro de tick), no al
    // entrar: así el doble-montaje de StrictMode (efecto→cleanup→efecto, que cancela
    // el rAF pendiente) reprograma en el segundo efecto en vez de quedar bloqueado.
    // page/setPage/pageSize se leen en el momento del disparo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, isLoading, enabled, resetKey]);
}
